// src/utils/mockDataSuppliers.ts
// Supplier registry, employee PPE sizing, uniform/boot orders, consumables
// Includes depot-aware local supplier suggestion engine

// ─────────────────────────────────────────────────────────────────
// SUPPLIER CATEGORIES
// ─────────────────────────────────────────────────────────────────
export type SupplierCategory =
  | 'ELECTRICAL_SIGNALS'
  | 'CIVIL_MATERIALS'
  | 'TRAFFIC_CONTROL'
  | 'PPE_UNIFORMS'
  | 'SAFETY_EQUIPMENT'
  | 'PLANT_HIRE'
  | 'FASTENERS_CONSUMABLES'
  | 'OTHER'

export const SUPPLIER_CATEGORY_META: Record<SupplierCategory, { label: string; bg: string; text: string }> = {
  ELECTRICAL_SIGNALS:      { label: 'Electrical & Signals',  bg: 'bg-blue-100',   text: 'text-blue-700' },
  CIVIL_MATERIALS:         { label: 'Civil materials',       bg: 'bg-teal-100',   text: 'text-teal-700' },
  TRAFFIC_CONTROL:         { label: 'Traffic control',       bg: 'bg-orange-100', text: 'text-orange-700' },
  PPE_UNIFORMS:            { label: 'PPE & uniforms',        bg: 'bg-purple-100', text: 'text-purple-700' },
  SAFETY_EQUIPMENT:        { label: 'Safety equipment',      bg: 'bg-red-100',    text: 'text-red-700' },
  PLANT_HIRE:              { label: 'Plant & equipment hire',bg: 'bg-amber-100',  text: 'text-amber-700' },
  FASTENERS_CONSUMABLES:   { label: 'Fasteners & consumables',bg:'bg-gray-100',  text: 'text-gray-600' },
  OTHER:                   { label: 'Other',                 bg: 'bg-gray-100',   text: 'text-gray-500' },
}

// ─────────────────────────────────────────────────────────────────
// SUPPLIER TYPE
// ─────────────────────────────────────────────────────────────────
export type SupplierStatus = 'ACTIVE' | 'INACTIVE' | 'PENDING_APPROVAL'

export type Supplier = {
  id: string
  companyName: string
  tradingName: string | null
  abn: string
  categories: SupplierCategory[]
  address: string; suburb: string; state: string; postcode: string
  gpsLat: number; gpsLng: number
  phone: string; email: string; website: string | null
  contactName: string; contactRole: string; contactDirect: string | null
  status: SupplierStatus
  isPreferred: boolean               // vetted and preferred by MCB CE
  isCompanyUniformSupplier: boolean  // supplies company-branded/logo uniforms
  rating: 1 | 2 | 3 | 4 | 5
  leadTimeDays: number
  paymentTermsDays: number
  notes: string | null
  addedById: string; addedByName: string; addedAt: string
  lastOrderDate: string | null
  approvedForJobs: string[]          // [] = all jobs
  // distance from each depot (km) for proximity suggestions
  distanceFromDepots: { SC: number; BNE: number }
}

// ─────────────────────────────────────────────────────────────────
// SUPPLIER REGISTRY
// ─────────────────────────────────────────────────────────────────
export const MOCK_SUPPLIERS: Supplier[] = [

  // ── Electrical & Signals ────────────────────────────────────
  {
    id: 'sup001', companyName: 'Rexel Australia Pty Ltd', tradingName: 'Rexel',
    abn: '40 004 763 051', categories: ['ELECTRICAL_SIGNALS', 'FASTENERS_CONSUMABLES'],
    address: '12 Gateway Dr', suburb: 'Noosaville', state: 'QLD', postcode: '4566',
    gpsLat: -26.3888, gpsLng: 153.0611,
    phone: '07 5449 7800', email: 'noosaville@rexel.com.au', website: 'rexel.com.au',
    contactName: 'Barry Walsh', contactRole: 'Account Manager', contactDirect: '0412 554 321',
    status: 'ACTIVE', isPreferred: true, isCompanyUniformSupplier: false,
    rating: 5, leadTimeDays: 2, paymentTermsDays: 30,
    notes: 'Primary electrical supplier for SC depot. 60-day price hold available on bulk cable orders.',
    addedById: 'u2', addedByName: 'Jane Smith', addedAt: '2026-01-15T00:00:00Z',
    lastOrderDate: '2026-06-15', approvedForJobs: [],
    distanceFromDepots: { SC: 18, BNE: 112 },
  },
  {
    id: 'sup002', companyName: 'Anixter Australia Pty Ltd', tradingName: 'Anixter',
    abn: '38 060 402 756', categories: ['ELECTRICAL_SIGNALS'],
    address: '19 Holt St', suburb: 'Pinkenba', state: 'QLD', postcode: '4008',
    gpsLat: -27.4316, gpsLng: 153.1022,
    phone: '07 3860 1200', email: 'brisbane@anixter.com.au', website: 'anixter.com',
    contactName: 'Sandra Nguyen', contactRole: 'State Account Manager', contactDirect: '0422 871 904',
    status: 'ACTIVE', isPreferred: true, isCompanyUniformSupplier: false,
    rating: 4, leadTimeDays: 3, paymentTermsDays: 30,
    notes: 'Preferred for multi-core cable and ITS cabling. National accounts pricing.',
    addedById: 'u2', addedByName: 'Jane Smith', addedAt: '2026-01-15T00:00:00Z',
    lastOrderDate: '2026-06-10', approvedForJobs: [],
    distanceFromDepots: { SC: 95, BNE: 12 },
  },
  {
    id: 'sup003', companyName: 'Schneider Electric Australia', tradingName: 'Schneider Electric',
    abn: '20 004 666 969', categories: ['ELECTRICAL_SIGNALS'],
    address: '78 Waterway Dr', suburb: 'Coomera', state: 'QLD', postcode: '4209',
    gpsLat: -27.8654, gpsLng: 153.3101,
    phone: '07 5502 8800', email: 'qld@se.com', website: 'se.com/au',
    contactName: 'Paul Drennan', contactRole: 'Regional Sales', contactDirect: '0411 228 441',
    status: 'ACTIVE', isPreferred: false, isCompanyUniformSupplier: false,
    rating: 4, leadTimeDays: 7, paymentTermsDays: 45,
    notes: 'SCATS controller and traffic signal cabinets — TMR approved product range.',
    addedById: 'u6', addedByName: 'Mark Wilson', addedAt: '2026-02-10T00:00:00Z',
    lastOrderDate: '2026-05-20', approvedForJobs: [],
    distanceFromDepots: { SC: 75, BNE: 65 },
  },

  // ── Civil Materials ──────────────────────────────────────────
  {
    id: 'sup004', companyName: 'Hanson Construction Materials Pty Ltd', tradingName: 'Hanson',
    abn: '60 008 163 263', categories: ['CIVIL_MATERIALS'],
    address: '42 Production Ave', suburb: 'Warana', state: 'QLD', postcode: '4575',
    gpsLat: -26.7142, gpsLng: 153.0888,
    phone: '07 5493 4100', email: 'sc@hanson.com.au', website: 'hanson.com.au',
    contactName: 'Tom Berry', contactRole: 'Sales Representative', contactDirect: '0438 119 004',
    status: 'ACTIVE', isPreferred: true, isCompanyUniformSupplier: false,
    rating: 4, leadTimeDays: 1, paymentTermsDays: 30,
    notes: 'Concrete, aggregates and pre-mix for SC region. Same-day delivery available within 20km.',
    addedById: 'u2', addedByName: 'Jane Smith', addedAt: '2026-01-20T00:00:00Z',
    lastOrderDate: '2026-06-12', approvedForJobs: [],
    distanceFromDepots: { SC: 8, BNE: 100 },
  },
  {
    id: 'sup005', companyName: 'Iplex Pipelines Australia Pty Ltd', tradingName: 'Iplex',
    abn: '29 007 226 489', categories: ['CIVIL_MATERIALS'],
    address: '28 Flinders Parade', suburb: 'North Lakes', state: 'QLD', postcode: '4509',
    gpsLat: -27.2144, gpsLng: 153.0211,
    phone: '07 3480 3200', email: 'qld@iplex.com.au', website: 'iplex.com.au',
    contactName: 'Kim Hartley', contactRole: 'Account Manager', contactDirect: '0408 334 902',
    status: 'ACTIVE', isPreferred: true, isCompanyUniformSupplier: false,
    rating: 4, leadTimeDays: 3, paymentTermsDays: 30,
    notes: 'UPVC conduit, cable pits, and pipe fittings. Holds stock for standard civil items.',
    addedById: 'u6', addedByName: 'Mark Wilson', addedAt: '2026-02-01T00:00:00Z',
    lastOrderDate: '2026-06-08', approvedForJobs: [],
    distanceFromDepots: { SC: 55, BNE: 28 },
  },

  // ── Traffic Control Equipment ────────────────────────────────
  {
    id: 'sup006', companyName: 'Traffic Management Solutions QLD Pty Ltd', tradingName: 'TMS QLD',
    abn: '73 148 892 012', categories: ['TRAFFIC_CONTROL'],
    address: '5 Machinery Dr', suburb: 'Kunda Park', state: 'QLD', postcode: '4556',
    gpsLat: -26.6621, gpsLng: 153.0508,
    phone: '07 5476 5500', email: 'info@tmsqld.com.au', website: 'tmsqld.com.au',
    contactName: 'Brett Logan', contactRole: 'Director', contactDirect: '0421 772 889',
    status: 'ACTIVE', isPreferred: true, isCompanyUniformSupplier: false,
    rating: 5, leadTimeDays: 1, paymentTermsDays: 14,
    notes: 'TC signage, barriers, cones, delineators. Same-day for SC area. Good for urgent replacements.',
    addedById: 'u2', addedByName: 'Jane Smith', addedAt: '2026-01-15T00:00:00Z',
    lastOrderDate: '2026-06-14', approvedForJobs: [],
    distanceFromDepots: { SC: 14, BNE: 105 },
  },

  // ── PPE & Uniforms ───────────────────────────────────────────
  {
    id: 'sup007', companyName: 'Total Image Group Pty Ltd', tradingName: 'Total Image Group',
    abn: '50 082 433 978', categories: ['PPE_UNIFORMS'],
    address: '22 Brandl St', suburb: 'Eight Mile Plains', state: 'QLD', postcode: '4113',
    gpsLat: -27.5811, gpsLng: 153.0966,
    phone: '07 3340 5500', email: 'workwear@totalimage.com.au', website: 'totalimage.com.au',
    contactName: 'Rachel Hart', contactRole: 'Account Manager — Corporate', contactDirect: '0417 550 221',
    status: 'ACTIVE', isPreferred: true, isCompanyUniformSupplier: true,
    rating: 5, leadTimeDays: 10, paymentTermsDays: 30,
    notes: 'PREFERRED UNIFORM SUPPLIER — holds MCB Civil & Electrical branded templates for shirts, jackets and pants. Embroidery and screen print in-house. Minimum order 5 units per item.',
    addedById: 'u1', addedByName: 'System Admin', addedAt: '2026-01-01T00:00:00Z',
    lastOrderDate: '2026-05-10', approvedForJobs: [],
    distanceFromDepots: { SC: 90, BNE: 15 },
  },
  {
    id: 'sup008', companyName: 'Blackwoods (Bunzl Safety & Industrial)', tradingName: 'Blackwoods',
    abn: '60 001 271 739', categories: ['PPE_UNIFORMS', 'SAFETY_EQUIPMENT', 'FASTENERS_CONSUMABLES'],
    address: '31 Enterprise St', suburb: 'Kunda Park', state: 'QLD', postcode: '4556',
    gpsLat: -26.6588, gpsLng: 153.0471,
    phone: '07 5476 8900', email: 'sc@blackwoods.com.au', website: 'blackwoods.com.au',
    contactName: 'Claire Johnson', contactRole: 'Branch Manager', contactDirect: '0402 663 217',
    status: 'ACTIVE', isPreferred: true, isCompanyUniformSupplier: false,
    rating: 4, leadTimeDays: 2, paymentTermsDays: 30,
    notes: 'Local SC branch. Non-branded PPE, safety equipment, and site consumables. Good for urgent boots and hi-vis. Online ordering available on account.',
    addedById: 'u2', addedByName: 'Jane Smith', addedAt: '2026-01-15T00:00:00Z',
    lastOrderDate: '2026-06-01', approvedForJobs: [],
    distanceFromDepots: { SC: 15, BNE: 104 },
  },
  {
    id: 'sup009', companyName: 'SafeWorx Pty Ltd', tradingName: 'SafeWorx',
    abn: '42 162 004 811', categories: ['PPE_UNIFORMS', 'SAFETY_EQUIPMENT'],
    address: '120 Holt St', suburb: 'Pinkenba', state: 'QLD', postcode: '4008',
    gpsLat: -27.4322, gpsLng: 153.1044,
    phone: '07 3621 4400', email: 'orders@safeworx.com.au', website: 'safeworx.com.au',
    contactName: 'Matt Giordano', contactRole: 'Sales', contactDirect: null,
    status: 'ACTIVE', isPreferred: false, isCompanyUniformSupplier: false,
    rating: 3, leadTimeDays: 4, paymentTermsDays: 14,
    notes: 'Brisbane branch only. Fallback for Brisbane-area jobs if Blackwoods is out of stock.',
    addedById: 'u2', addedByName: 'Jane Smith', addedAt: '2026-03-01T00:00:00Z',
    lastOrderDate: '2026-04-15', approvedForJobs: [],
    distanceFromDepots: { SC: 94, BNE: 11 },
  },

  // ── Plant Hire ───────────────────────────────────────────────
  {
    id: 'sup010', companyName: 'Kennards Hire Pty Ltd', tradingName: 'Kennards Hire',
    abn: '94 001 571 901', categories: ['PLANT_HIRE'],
    address: '6 Kayleigh Dr', suburb: 'Maroochydore', state: 'QLD', postcode: '4558',
    gpsLat: -26.6501, gpsLng: 153.0722,
    phone: '07 5443 1212', email: 'maroochydore@kennards.com.au', website: 'kh.com.au',
    contactName: 'Brad Cutler', contactRole: 'Branch Manager', contactDirect: '0411 228 990',
    status: 'ACTIVE', isPreferred: true, isCompanyUniformSupplier: false,
    rating: 4, leadTimeDays: 1, paymentTermsDays: 14,
    notes: 'EWP boom, scissor lifts, compaction equipment. SC branch — local delivery. 10% account discount.',
    addedById: 'u2', addedByName: 'Jane Smith', addedAt: '2026-01-15T00:00:00Z',
    lastOrderDate: '2026-06-16', approvedForJobs: [],
    distanceFromDepots: { SC: 20, BNE: 98 },
  },
  {
    id: 'sup011', companyName: 'Coates Hire Operations Pty Ltd', tradingName: 'Coates',
    abn: '68 004 079 297', categories: ['PLANT_HIRE'],
    address: '47 Proprietary Pl', suburb: 'Tingalpa', state: 'QLD', postcode: '4173',
    gpsLat: -27.5131, gpsLng: 153.1311,
    phone: '07 3390 1900', email: 'tingalpa@coates.com.au', website: 'coates.com.au',
    contactName: 'Dave Holt', contactRole: 'Hire Coordinator', contactDirect: null,
    status: 'ACTIVE', isPreferred: false, isCompanyUniformSupplier: false,
    rating: 3, leadTimeDays: 1, paymentTermsDays: 14,
    notes: 'Brisbane east. Used when Kennards SC is unavailable or for Brisbane-south jobs.',
    addedById: 'u5', addedByName: 'Mike Walsh', addedAt: '2026-04-01T00:00:00Z',
    lastOrderDate: '2026-05-28', approvedForJobs: [],
    distanceFromDepots: { SC: 98, BNE: 18 },
  },

  // ── Fasteners / Consumables ──────────────────────────────────
  {
    id: 'sup012', companyName: 'Wurth Australia Pty Ltd', tradingName: 'Würth',
    abn: '66 010 513 167', categories: ['FASTENERS_CONSUMABLES'],
    address: 'Delivery van — SC area', suburb: 'Maroochydore', state: 'QLD', postcode: '4558',
    gpsLat: -26.6512, gpsLng: 153.0709,
    phone: '1300 987 884', email: 'sc@wurth.com.au', website: 'wurth.com.au',
    contactName: 'Karl Muller', contactRole: 'Area Representative', contactDirect: '0421 887 440',
    status: 'ACTIVE', isPreferred: true, isCompanyUniformSupplier: false,
    rating: 5, leadTimeDays: 1, paymentTermsDays: 30,
    notes: 'Van-stocked mobile rep covers SC area. Fasteners, sealants, cable management, tools. Weekly call cycle — ideal for site top-ups.',
    addedById: 'u2', addedByName: 'Jane Smith', addedAt: '2026-01-15T00:00:00Z',
    lastOrderDate: '2026-06-10', approvedForJobs: [],
    distanceFromDepots: { SC: 20, BNE: 98 },
  },

  // ── Pending approval (just added by PC) ──────────────────────
  {
    id: 'sup013', companyName: 'GC Civil Supplies Pty Ltd', tradingName: null,
    abn: '81 198 320 045', categories: ['CIVIL_MATERIALS', 'FASTENERS_CONSUMABLES'],
    address: '14 Kortum Dr', suburb: 'Burleigh Heads', state: 'QLD', postcode: '4220',
    gpsLat: -28.0911, gpsLng: 153.4378,
    phone: '07 5535 8800', email: 'orders@gccivil.com.au', website: null,
    contactName: 'Tony Ferraro', contactRole: 'Owner / Sales', contactDirect: '0414 221 808',
    status: 'PENDING_APPROVAL',
    isPreferred: false, isCompanyUniformSupplier: false,
    rating: 4, leadTimeDays: 1, paymentTermsDays: 7,
    notes: 'Suggested by Mike Walsh for j3 (Gold Coast Hwy). Local GC supplier — competitive on UPVC conduit. Pending Tier B approval.',
    addedById: 'u5', addedByName: 'Mike Walsh', addedAt: '2026-06-16T14:00:00Z',
    lastOrderDate: null, approvedForJobs: ['j3'],
    distanceFromDepots: { SC: 80, BNE: 88 },
  },
]

// ─────────────────────────────────────────────────────────────────
// LOCAL SUPPLIER SUGGESTION ENGINE
// ─────────────────────────────────────────────────────────────────
// Job location mapping to nearest depot/region
const JOB_REGION: Record<string, { depot: 'SC' | 'BNE'; maxKm: number }> = {
  j1: { depot: 'BNE', maxKm: 40 },  // Kessels Rd — south Brisbane
  j2: { depot: 'BNE', maxKm: 40 },  // Pacific Mwy — south east Brisbane
  j3: { depot: 'BNE', maxKm: 110 }, // Gold Coast Hwy — GC (use BNE + GC)
  j4: { depot: 'BNE', maxKm: 20 },  // Annerley — inner Brisbane
  j5: { depot: 'BNE', maxKm: 35 },  // Ipswich Rd — western Brisbane
  j6: { depot: 'SC',  maxKm: 60 },  // Sunshine Coast job
}

export function suggestSuppliers(
  jobId: string,
  category: SupplierCategory,
  limit = 4
): Supplier[] {
  const region = JOB_REGION[jobId] ?? { depot: 'SC', maxKm: 50 }
  const relevant = MOCK_SUPPLIERS.filter(s =>
    s.status === 'ACTIVE' &&
    s.categories.includes(category)
  )
  return relevant
    .map(s => ({
      supplier: s,
      score:
        (s.isPreferred ? 100 : 0) +
        (s.distanceFromDepots[region.depot] <= region.maxKm ? 50 : 0) +
        (s.rating * 10) -
        (s.distanceFromDepots[region.depot] * 0.2),
    }))
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(r => r.supplier)
}

// ─────────────────────────────────────────────────────────────────
// EMPLOYEE PPE SIZING
// ─────────────────────────────────────────────────────────────────
export type ShirtSize = 'XS'|'S'|'M'|'L'|'XL'|'2XL'|'3XL'

export type EmployeePPESize = {
  employeeId: string
  shirtSize: ShirtSize | null
  pantSize: string | null       // '32x32' (men) or '12' (women)
  shoeSize: number | null       // AU size
  hiVisVestSize: string | null
  hardHatSize: 'Standard' | 'Large' | null
  gloveSize: 'S'|'M'|'L'|'XL' | null
  updatedAt: string
  updatedById: string; updatedByName: string
  notes: string | null
}

export const MOCK_EMPLOYEE_SIZES: EmployeePPESize[] = [
  { employeeId: 'e1', shirtSize: 'XL',  pantSize: '34x32', shoeSize: 10, hiVisVestSize: 'XL',  hardHatSize: 'Standard', gloveSize: 'L',  updatedAt: '2026-02-01T00:00:00Z', updatedById: 'u2', updatedByName: 'Jane Smith',  notes: null },
  { employeeId: 'e2', shirtSize: 'S',   pantSize: '10',    shoeSize: 6,  hiVisVestSize: 'S',   hardHatSize: 'Standard', gloveSize: 'S',  updatedAt: '2026-02-01T00:00:00Z', updatedById: 'u2', updatedByName: 'Jane Smith',  notes: null },
  { employeeId: 'e3', shirtSize: 'L',   pantSize: '32x30', shoeSize: 9,  hiVisVestSize: 'L',   hardHatSize: 'Standard', gloveSize: 'M',  updatedAt: '2026-02-01T00:00:00Z', updatedById: 'u2', updatedByName: 'Jane Smith',  notes: null },
  { employeeId: 'e4', shirtSize: 'XL',  pantSize: '36x32', shoeSize: 11, hiVisVestSize: 'XL',  hardHatSize: 'Large',    gloveSize: 'XL', updatedAt: '2026-02-01T00:00:00Z', updatedById: 'u2', updatedByName: 'Jane Smith',  notes: null },
  { employeeId: 'e5', shirtSize: 'M',   pantSize: '12',    shoeSize: 7,  hiVisVestSize: 'M',   hardHatSize: 'Standard', gloveSize: 'S',  updatedAt: '2026-02-01T00:00:00Z', updatedById: 'u2', updatedByName: 'Jane Smith',  notes: null },
  { employeeId: 'e6', shirtSize: 'M',   pantSize: '30x30', shoeSize: 9,  hiVisVestSize: 'M',   hardHatSize: 'Standard', gloveSize: 'M',  updatedAt: '2026-03-01T00:00:00Z', updatedById: 'u2', updatedByName: 'Jane Smith',  notes: 'First uniform issue — new starter' },
  { employeeId: 'e7', shirtSize: '2XL', pantSize: '38x32', shoeSize: 12, hiVisVestSize: '2XL', hardHatSize: 'Large',    gloveSize: 'XL', updatedAt: '2026-02-01T00:00:00Z', updatedById: 'u2', updatedByName: 'Jane Smith',  notes: null },
  { employeeId: 'e8', shirtSize: 'S',   pantSize: '8',     shoeSize: 6,  hiVisVestSize: 'S',   hardHatSize: 'Standard', gloveSize: 'S',  updatedAt: '2026-02-01T00:00:00Z', updatedById: 'u2', updatedByName: 'Jane Smith',  notes: null },
]

// ─────────────────────────────────────────────────────────────────
// PPE / UNIFORM ORDERS
// ─────────────────────────────────────────────────────────────────
export type PPEItemType =
  | 'SHIRT_COMPANY'       // company-branded long-sleeve
  | 'SHIRT_HI_VIS'        // hi-vis branded work shirt
  | 'PANTS_CARGO'         // cargo work pants
  | 'PANTS_HI_VIS'        // hi-vis work pants
  | 'JACKET_SOFTSHELL'    // softshell company jacket
  | 'JACKET_RAIN'         // rain jacket
  | 'BOOTS_STEEL_CAP'     // steel cap safety boots
  | 'BOOTS_GUMBOOTS'      // gumboots
  | 'HI_VIS_VEST'         // hi-vis vest
  | 'HARD_HAT'            // hard hat
  | 'SAFETY_GLASSES'      // safety glasses
  | 'GLOVES'              // work gloves
  | 'SUNSCREEN_SPF50'     // consumable
  | 'EAR_PLUGS'           // consumable
  | 'OTHER'

export const PPE_ITEM_META: Record<PPEItemType, { label: string; isCompanyBranded: boolean; typicalCost: number }> = {
  SHIRT_COMPANY:   { label: 'Work shirt (company branded)',      isCompanyBranded: true,  typicalCost: 68.00 },
  SHIRT_HI_VIS:    { label: 'Hi-vis shirt (company branded)',    isCompanyBranded: true,  typicalCost: 74.00 },
  PANTS_CARGO:     { label: 'Cargo work pants',                  isCompanyBranded: false, typicalCost: 89.00 },
  PANTS_HI_VIS:    { label: 'Hi-vis work pants',                 isCompanyBranded: false, typicalCost: 94.00 },
  JACKET_SOFTSHELL:{ label: 'Softshell jacket (company branded)',isCompanyBranded: true,  typicalCost: 145.00 },
  JACKET_RAIN:     { label: 'Rain jacket',                       isCompanyBranded: false, typicalCost: 112.00 },
  BOOTS_STEEL_CAP: { label: 'Safety boots (steel cap)',          isCompanyBranded: false, typicalCost: 178.00 },
  BOOTS_GUMBOOTS:  { label: 'Gumboots',                          isCompanyBranded: false, typicalCost: 48.00 },
  HI_VIS_VEST:     { label: 'Hi-vis vest',                       isCompanyBranded: false, typicalCost: 24.00 },
  HARD_HAT:        { label: 'Hard hat',                          isCompanyBranded: false, typicalCost: 38.00 },
  SAFETY_GLASSES:  { label: 'Safety glasses',                    isCompanyBranded: false, typicalCost: 18.00 },
  GLOVES:          { label: 'Work gloves',                       isCompanyBranded: false, typicalCost: 22.00 },
  SUNSCREEN_SPF50: { label: 'Sunscreen SPF50 (consumable)',       isCompanyBranded: false, typicalCost: 14.00 },
  EAR_PLUGS:       { label: 'Ear plugs (box 200)',                isCompanyBranded: false, typicalCost: 28.00 },
  OTHER:           { label: 'Other PPE',                          isCompanyBranded: false, typicalCost: 0 },
}

export type PPEOrderStatus = 'PENDING_APPROVAL' | 'APPROVED' | 'ORDERED' | 'SHIPPED' | 'DELIVERED' | 'PARTIAL'

export const PPE_ORDER_STATUS_META: Record<PPEOrderStatus, { label: string; bg: string; text: string }> = {
  PENDING_APPROVAL: { label: 'Pending approval', bg: 'bg-gray-100',   text: 'text-gray-600' },
  APPROVED:         { label: 'Approved',         bg: 'bg-blue-100',   text: 'text-blue-700' },
  ORDERED:          { label: 'Ordered',          bg: 'bg-purple-100', text: 'text-purple-700' },
  SHIPPED:          { label: 'Shipped',          bg: 'bg-amber-100',  text: 'text-amber-700' },
  DELIVERED:        { label: 'Delivered',        bg: 'bg-green-100',  text: 'text-green-700' },
  PARTIAL:          { label: 'Partial delivery', bg: 'bg-orange-100', text: 'text-orange-700' },
}

export type PPEOrderLine = {
  itemType: PPEItemType
  description: string
  quantity: number
  size: string
  unitCost: number
  totalCost: number
  isCompanyBranded: boolean
  deliveredQty: number
}

export type PPEOrder = {
  id: string
  reference: string
  employeeId: string; employeeName: string
  supplierId: string; supplierName: string
  isCompanyBranded: boolean  // whether this order requires branded items
  lines: PPEOrderLine[]
  totalCost: number
  status: PPEOrderStatus
  reason: string   // 'New starter', 'Annual reissue', 'Replacement', 'Size change', 'Boot replacement'
  requestedById: string; requestedByName: string; requestedAt: string
  approvedById: string | null; approvedByName: string | null; approvedAt: string | null
  orderedAt: string | null; estimatedDelivery: string | null; deliveredAt: string | null
  notes: string | null
}

export const MOCK_PPE_ORDERS: PPEOrder[] = [
  // Tony Rivera — annual reissue
  {
    id: 'ppe001', reference: 'PPE-2026-001',
    employeeId: 'e1', employeeName: 'Tony Rivera',
    supplierId: 'sup007', supplierName: 'Total Image Group',
    isCompanyBranded: true,
    lines: [
      { itemType: 'SHIRT_COMPANY', description: 'Work shirt L/S company logo', quantity: 3, size: 'XL', unitCost: 68.00, totalCost: 204.00, isCompanyBranded: true, deliveredQty: 3 },
      { itemType: 'SHIRT_HI_VIS',  description: 'Hi-vis shirt company logo',   quantity: 2, size: 'XL', unitCost: 74.00, totalCost: 148.00, isCompanyBranded: true, deliveredQty: 2 },
      { itemType: 'PANTS_HI_VIS',  description: 'Hi-vis cargo pants',          quantity: 2, size: '34x32', unitCost: 94.00, totalCost: 188.00, isCompanyBranded: false, deliveredQty: 2 },
    ],
    totalCost: 540.00, status: 'DELIVERED', reason: 'Annual reissue',
    requestedById: 'u4', requestedByName: 'Sarah Lee', requestedAt: '2026-05-01T00:00:00Z',
    approvedById: 'u2', approvedByName: 'Jane Smith', approvedAt: '2026-05-02T00:00:00Z',
    orderedAt: '2026-05-02T00:00:00Z', estimatedDelivery: '2026-05-16', deliveredAt: '2026-05-14',
    notes: null,
  },
  // Dave Fielding — new starter
  {
    id: 'ppe002', reference: 'PPE-2026-002',
    employeeId: 'e6', employeeName: 'Dave Fielding',
    supplierId: 'sup007', supplierName: 'Total Image Group',
    isCompanyBranded: true,
    lines: [
      { itemType: 'SHIRT_COMPANY', description: 'Work shirt L/S company logo', quantity: 3, size: 'M', unitCost: 68.00, totalCost: 204.00, isCompanyBranded: true, deliveredQty: 3 },
      { itemType: 'SHIRT_HI_VIS',  description: 'Hi-vis shirt company logo',   quantity: 2, size: 'M', unitCost: 74.00, totalCost: 148.00, isCompanyBranded: true, deliveredQty: 2 },
      { itemType: 'PANTS_CARGO',   description: 'Cargo work pants',            quantity: 2, size: '30x30', unitCost: 89.00, totalCost: 178.00, isCompanyBranded: false, deliveredQty: 2 },
      { itemType: 'JACKET_SOFTSHELL', description: 'Softshell jacket logo',    quantity: 1, size: 'M', unitCost: 145.00, totalCost: 145.00, isCompanyBranded: true, deliveredQty: 1 },
    ],
    totalCost: 675.00, status: 'DELIVERED', reason: 'New starter',
    requestedById: 'u5', requestedByName: 'Mike Walsh', requestedAt: '2026-03-01T00:00:00Z',
    approvedById: 'u2', approvedByName: 'Jane Smith', approvedAt: '2026-03-02T00:00:00Z',
    orderedAt: '2026-03-02T00:00:00Z', estimatedDelivery: '2026-03-16', deliveredAt: '2026-03-15',
    notes: 'New apprentice starter pack.',
  },
  // Tony Rivera — boot replacement
  {
    id: 'ppe003', reference: 'PPE-2026-003',
    employeeId: 'e1', employeeName: 'Tony Rivera',
    supplierId: 'sup008', supplierName: 'Blackwoods',
    isCompanyBranded: false,
    lines: [
      { itemType: 'BOOTS_STEEL_CAP', description: "Blundstone 8' steel cap boot", quantity: 1, size: 'US 10', unitCost: 178.00, totalCost: 178.00, isCompanyBranded: false, deliveredQty: 0 },
    ],
    totalCost: 178.00, status: 'SHIPPED', reason: 'Boot replacement',
    requestedById: 'u4', requestedByName: 'Sarah Lee', requestedAt: '2026-06-10T00:00:00Z',
    approvedById: 'u2', approvedByName: 'Jane Smith', approvedAt: '2026-06-10T12:00:00Z',
    orderedAt: '2026-06-10T00:00:00Z', estimatedDelivery: '2026-06-18', deliveredAt: null,
    notes: 'Current boots showing sole separation. Safety risk — priority order.',
  },
  // Sam Nguyen — pending approval
  {
    id: 'ppe004', reference: 'PPE-2026-004',
    employeeId: 'e3', employeeName: 'Sam Nguyen',
    supplierId: 'sup007', supplierName: 'Total Image Group',
    isCompanyBranded: true,
    lines: [
      { itemType: 'JACKET_SOFTSHELL', description: 'Softshell jacket logo',    quantity: 1, size: 'L', unitCost: 145.00, totalCost: 145.00, isCompanyBranded: true, deliveredQty: 0 },
      { itemType: 'JACKET_RAIN',       description: 'Rain jacket',             quantity: 1, size: 'L', unitCost: 112.00, totalCost: 112.00, isCompanyBranded: false, deliveredQty: 0 },
    ],
    totalCost: 257.00, status: 'PENDING_APPROVAL', reason: 'Winter issue',
    requestedById: 'u5', requestedByName: 'Mike Walsh', requestedAt: '2026-06-17T09:00:00Z',
    approvedById: null, approvedByName: null, approvedAt: null,
    orderedAt: null, estimatedDelivery: null, deliveredAt: null,
    notes: 'Deployed to Brisbane for HV work — cold nights. Requesting jackets before Jun 25 if possible.',
  },
]

// ─────────────────────────────────────────────────────────────────
// CONSUMABLES
// ─────────────────────────────────────────────────────────────────
export type ConsumableCategory = 'SITE_FIXINGS' | 'SEALANTS_ADHESIVES' | 'MARKING' | 'SAFETY_DISPOSABLE' | 'CLEANING' | 'OTHER'

export const CONSUMABLE_CAT_META: Record<ConsumableCategory, { label: string; bg: string; text: string }> = {
  SITE_FIXINGS:        { label: 'Site fixings',         bg: 'bg-gray-100',  text: 'text-gray-700' },
  SEALANTS_ADHESIVES:  { label: 'Sealants & adhesives', bg: 'bg-blue-50',   text: 'text-blue-700' },
  MARKING:             { label: 'Marking & labelling',  bg: 'bg-amber-100', text: 'text-amber-700' },
  SAFETY_DISPOSABLE:   { label: 'Safety (disposable)',  bg: 'bg-red-100',   text: 'text-red-700' },
  CLEANING:            { label: 'Cleaning',             bg: 'bg-teal-100',  text: 'text-teal-700' },
  OTHER:               { label: 'Other',                bg: 'bg-gray-100',  text: 'text-gray-500' },
}

export type ConsumableOrderStatus = 'PENDING' | 'ORDERED' | 'DELIVERED'

export type ConsumableOrder = {
  id: string; reference: string
  description: string; category: ConsumableCategory
  quantity: number; unit: string; supplierId: string; supplierName: string
  unitCost: number; totalCost: number
  status: ConsumableOrderStatus
  requestedById: string; requestedByName: string; requestedAt: string
  deliveredAt: string | null
}

export const MOCK_CONSUMABLE_ORDERS: ConsumableOrder[] = [
  { id: 'con001', reference: 'CON-2026-001', description: 'Cable ties 200mm (bag 100)', category: 'SITE_FIXINGS', quantity: 10, unit: 'bag', supplierId: 'sup012', supplierName: 'Würth', unitCost: 12.40, totalCost: 124.00, status: 'DELIVERED', requestedById: 'u4', requestedByName: 'Sarah Lee', requestedAt: '2026-06-01T00:00:00Z', deliveredAt: '2026-06-03' },
  { id: 'con002', reference: 'CON-2026-002', description: 'Silicone sealant grey (tube)', category: 'SEALANTS_ADHESIVES', quantity: 20, unit: 'tube', supplierId: 'sup012', supplierName: 'Würth', unitCost: 8.80, totalCost: 176.00, status: 'DELIVERED', requestedById: 'u4', requestedByName: 'Sarah Lee', requestedAt: '2026-06-01T00:00:00Z', deliveredAt: '2026-06-03' },
  { id: 'con003', reference: 'CON-2026-003', description: 'Aerosol marking paint — orange', category: 'MARKING', quantity: 12, unit: 'can', supplierId: 'sup008', supplierName: 'Blackwoods', unitCost: 9.20, totalCost: 110.40, status: 'DELIVERED', requestedById: 'u5', requestedByName: 'Mike Walsh', requestedAt: '2026-06-05T00:00:00Z', deliveredAt: '2026-06-07' },
  { id: 'con004', reference: 'CON-2026-004', description: 'Disposable nitrile gloves (box 100)', category: 'SAFETY_DISPOSABLE', quantity: 5, unit: 'box', supplierId: 'sup008', supplierName: 'Blackwoods', unitCost: 18.50, totalCost: 92.50, status: 'ORDERED', requestedById: 'u4', requestedByName: 'Sarah Lee', requestedAt: '2026-06-15T00:00:00Z', deliveredAt: null },
  { id: 'con005', reference: 'CON-2026-005', description: 'P2 dust masks (box 20)', category: 'SAFETY_DISPOSABLE', quantity: 3, unit: 'box', supplierId: 'sup008', supplierName: 'Blackwoods', unitCost: 32.00, totalCost: 96.00, status: 'PENDING', requestedById: 'u5', requestedByName: 'Mike Walsh', requestedAt: '2026-06-17T10:00:00Z', deliveredAt: null },
]

// ─────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────
export function getSuppliersByCategory(cat: SupplierCategory): Supplier[] {
  return MOCK_SUPPLIERS.filter(s => s.status === 'ACTIVE' && s.categories.includes(cat))
}

export function getEmployeeSize(employeeId: string): EmployeePPESize | null {
  return MOCK_EMPLOYEE_SIZES.find(s => s.employeeId === employeeId) ?? null
}

export function getPPEOrdersForEmployee(employeeId: string): PPEOrder[] {
  return MOCK_PPE_ORDERS.filter(o => o.employeeId === employeeId)
}

export function getPendingPPEApprovals(): PPEOrder[] {
  return MOCK_PPE_ORDERS.filter(o => o.status === 'PENDING_APPROVAL')
}

// ───────────────────────────────────────────────────────────────
// SUPPLIER PRODUCT CATALOGUE
// A05 Supplier Intelligence Agent — Supplementary Brief D2
// Extends the supplier register with structured product data:
// spec compliance, lead times, and delivery performance per product.
// ───────────────────────────────────────────────────────────────

export interface SupplierProduct {
  id:                string
  supplierId:        string
  supplierName:      string
  productCode:       string
  description:       string
  category:          SupplierCategory
  unit:              string
  unitPrice:         number       // ex GST
  // Spec compliance
  clientSpecCode:    string | null  // e.g. 'MRTS52 Cl.4.3' or 'AS/NZS 4191'
  isApprovedProduct: boolean
  approvedForClients: string[]     // e.g. ['TMR', 'MBRC', 'SCC']
  complianceNotes:   string | null
  // Lead time
  standardLeadDays:  number
  expressLeadDays:   number | null
  lastPriceDate:     string        // ISO date
  estimateRate:      number | null  // the rate in the current estimate (for price drift check)
  // Performance
  onTimeDeliveryRate: number       // 0–100 %
  lastOrderDate:     string | null
}

export const SUPPLIER_PRODUCTS: SupplierProduct[] = [
  // ── Rexel — Electrical & Signals ───────────────────────────────────────────────────
  { id: 'sp001', supplierId: 'sup001', supplierName: 'Rexel Australia', productCode: 'RX-SIG6O', description: 'Signal cable CU 6mm² twin-core orange sheath AS/NZS 5000.1', category: 'ELECTRICAL_SIGNALS', unit: 'm', unitPrice: 4.85, clientSpecCode: 'MRTS52 Cl.4.3', isApprovedProduct: true, approvedForClients: ['TMR','MBRC','SCC'], complianceNotes: 'Orange sheath required for buried signal cable per MRTS52. Confirm cut lengths on delivery.', standardLeadDays: 2, expressLeadDays: 1, lastPriceDate: '2026-07-15', estimateRate: 4.60, onTimeDeliveryRate: 94, lastOrderDate: '2026-07-10' },
  { id: 'sp002', supplierId: 'sup001', supplierName: 'Rexel Australia', productCode: 'RX-SIG4O', description: 'Signal cable CU 4mm² twin-core orange sheath', category: 'ELECTRICAL_SIGNALS', unit: 'm', unitPrice: 3.40, clientSpecCode: 'MRTS52 Cl.4.3', isApprovedProduct: true, approvedForClients: ['TMR','MBRC'], complianceNotes: null, standardLeadDays: 2, expressLeadDays: 1, lastPriceDate: '2026-07-15', estimateRate: 3.30, onTimeDeliveryRate: 94, lastOrderDate: '2026-06-20' },
  { id: 'sp003', supplierId: 'sup001', supplierName: 'Rexel Australia', productCode: 'RX-SH-LED-TF', description: 'LED Signal head 3-phase R/A/G AS/NZS 4191 TMR-approved', category: 'ELECTRICAL_SIGNALS', unit: 'ea', unitPrice: 342.00, clientSpecCode: 'AS/NZS 4191', isApprovedProduct: true, approvedForClients: ['TMR'], complianceNotes: 'Must carry TMR type approval certificate on delivery docket.', standardLeadDays: 5, expressLeadDays: 2, lastPriceDate: '2026-07-20', estimateRate: 316.00, onTimeDeliveryRate: 88, lastOrderDate: '2026-07-08' },
  { id: 'sp004', supplierId: 'sup001', supplierName: 'Rexel Australia', productCode: 'RX-COND50', description: 'Heavy duty conduit 50mm dia. AS/NZS 2053 UV-stabilised', category: 'ELECTRICAL_SIGNALS', unit: 'm', unitPrice: 3.20, clientSpecCode: 'AS/NZS 2053', isApprovedProduct: true, approvedForClients: ['TMR','MBRC','SCC'], complianceNotes: null, standardLeadDays: 3, expressLeadDays: 1, lastPriceDate: '2026-07-15', estimateRate: 3.20, onTimeDeliveryRate: 94, lastOrderDate: '2026-07-05' },
  // ── Anixter — Electrical ──────────────────────────────────────────────────────────
  { id: 'sp005', supplierId: 'sup002', supplierName: 'Anixter Australia', productCode: 'ANX-SH-LED-TF', description: 'LED Signal head 3-phase AS/NZS 4191 (alt. to Rexel)', category: 'ELECTRICAL_SIGNALS', unit: 'ea', unitPrice: 328.00, clientSpecCode: 'AS/NZS 4191', isApprovedProduct: true, approvedForClients: ['TMR'], complianceNotes: 'TMR type approval cert required. Preferred for bulk orders >20 units (better price).', standardLeadDays: 7, expressLeadDays: 3, lastPriceDate: '2026-07-18', estimateRate: 316.00, onTimeDeliveryRate: 91, lastOrderDate: '2026-05-15' },
  { id: 'sp006', supplierId: 'sup002', supplierName: 'Anixter Australia', productCode: 'ANX-SIG6O', description: 'Signal cable CU 6mm² orange sheath — cut to length', category: 'ELECTRICAL_SIGNALS', unit: 'm', unitPrice: 4.70, clientSpecCode: 'MRTS52 Cl.4.3', isApprovedProduct: true, approvedForClients: ['TMR','MBRC'], complianceNotes: null, standardLeadDays: 5, expressLeadDays: 2, lastPriceDate: '2026-07-18', estimateRate: 4.60, onTimeDeliveryRate: 91, lastOrderDate: '2026-04-22' },
  // ── Traffic Control ─────────────────────────────────────────────────────────────
  { id: 'sp007', supplierId: 'sup004', supplierName: 'Saferoads', productCode: 'SR-TC700', description: 'Traffic cone 700mm orange retroreflective banding AS/NZS 4602.1', category: 'TRAFFIC_CONTROL', unit: 'ea', unitPrice: 18.50, clientSpecCode: 'AS/NZS 4602.1', isApprovedProduct: true, approvedForClients: ['TMR','MBRC','SCC','TRC'], complianceNotes: 'Class 1 retroreflective sheeting required per MUTCD Part 3.', standardLeadDays: 7, expressLeadDays: 2, lastPriceDate: '2026-07-01', estimateRate: 18.00, onTimeDeliveryRate: 82, lastOrderDate: '2026-06-01' },
  { id: 'sp008', supplierId: 'sup005', supplierName: 'Alsco Safety', productCode: 'AC-TC700', description: 'Traffic cone 700mm Class 1 retroreflective — MUTCD Part 3 compliant', category: 'TRAFFIC_CONTROL', unit: 'ea', unitPrice: 19.80, clientSpecCode: 'AS/NZS 4602.1', isApprovedProduct: true, approvedForClients: ['TMR','MBRC','SCC'], complianceNotes: 'Express delivery available 2 business days. Good alternative when Saferoads lead time is too long.', standardLeadDays: 3, expressLeadDays: 2, lastPriceDate: '2026-07-10', estimateRate: 18.00, onTimeDeliveryRate: 96, lastOrderDate: '2026-07-12' },
  { id: 'sp009', supplierId: 'sup004', supplierName: 'Saferoads', productCode: 'SR-VMS-SOL', description: 'Variable Message Sign — solar powered MUTCD Part 3 compliant', category: 'TRAFFIC_CONTROL', unit: 'ea', unitPrice: 14500.00, clientSpecCode: 'MUTCD Part 3', isApprovedProduct: true, approvedForClients: ['TMR','MBRC'], complianceNotes: 'Hire available from Saferoads @ $280/day as alternative to purchase.', standardLeadDays: 14, expressLeadDays: null, lastPriceDate: '2026-06-01', estimateRate: 14000.00, onTimeDeliveryRate: 78, lastOrderDate: '2026-04-01' },
  // ── Civil materials ─────────────────────────────────────────────────────────────
  { id: 'sp010', supplierId: 'sup006', supplierName: 'Holcim Australia', productCode: 'HC-CONC-N32', description: 'Ready-mix concrete N32 — signal pole foundations', category: 'CIVIL_MATERIALS', unit: 'm³', unitPrice: 245.00, clientSpecCode: 'MRTS70 Cl.5 N32', isApprovedProduct: true, approvedForClients: ['TMR','MBRC','SCC'], complianceNotes: 'Batch plant must have NATA-endorsed QA system. Request batch ticket on delivery.', standardLeadDays: 1, expressLeadDays: 1, lastPriceDate: '2026-07-01', estimateRate: 235.00, onTimeDeliveryRate: 97, lastOrderDate: '2026-07-08' },
  { id: 'sp011', supplierId: 'sup006', supplierName: 'Holcim Australia', productCode: 'HC-BASE-20', description: 'DGB20 basecourse crushed rock — conduit trench backfill', category: 'CIVIL_MATERIALS', unit: 't', unitPrice: 62.00, clientSpecCode: 'MRTS05 Cl.4.2', isApprovedProduct: true, approvedForClients: ['TMR'], complianceNotes: 'Source must have valid quarry pre-qualification under MRTS05.', standardLeadDays: 2, expressLeadDays: 1, lastPriceDate: '2026-07-01', estimateRate: 62.00, onTimeDeliveryRate: 95, lastOrderDate: '2026-07-10' },
]

export function getProductsByCategory(cat: SupplierCategory): SupplierProduct[] {
  return SUPPLIER_PRODUCTS.filter(p => p.category === cat)
}

export function getProductsBySupplier(supplierId: string): SupplierProduct[] {
  return SUPPLIER_PRODUCTS.filter(p => p.supplierId === supplierId)
}

export function getNonCompliantProducts(): SupplierProduct[] {
  return SUPPLIER_PRODUCTS.filter(p => !p.isApprovedProduct)
}

export function getPriceDriftProducts(thresholdPct = 5): SupplierProduct[] {
  return SUPPLIER_PRODUCTS.filter(p => {
    if (!p.estimateRate) return false
    const drift = Math.abs((p.unitPrice - p.estimateRate) / p.estimateRate * 100)
    return drift >= thresholdPct
  })
}
